// Классификация качества ходов по алгоритму chess.com (реконструкция
// freechess/WintrCat): квадратичные пороги от prevEval, 11 категорий,
// матовые границы, Brilliant=жертва-висяк, Great=лучший после зевка.
// Порт из app.js v3 (~3076–3208 + brilliantGates ~2333).
import { Chess } from 'chess.js';
import type { GameNode } from './types';
import { applyLooseMove } from './move-detect';
import { uciToSan } from './eval-utils';
import {
  EvalStore,
  MATE_CP,
  engineArtifactsValid,
  moverCp,
  moverMate,
  nodeEvalKey,
  nodeFen,
  nodeMateWhite,
} from './evals';
import { PIECE_VALUES, findHangingPieces, hangingCaptureViable } from './hanging';

export type MoveQuality =
  | 'brilliant' | 'great' | 'best' | 'excellent' | 'good' | 'book'
  | 'forced' | 'inaccuracy' | 'mistake' | 'miss' | 'blunder';

export const Q_KEYS: MoveQuality[] = [
  'brilliant', 'great', 'best', 'excellent', 'good', 'book', 'forced', 'inaccuracy', 'mistake', 'miss', 'blunder',
];
export const Q_GLYPH: Record<MoveQuality, string> = {
  brilliant: '!!', great: '!', best: '★', excellent: '✦', good: '✓', book: '📖',
  forced: '⇉', inaccuracy: '?!', mistake: '?', miss: '✗', blunder: '??',
};

/** Единый палитр качества ходов (chess.com-подобный): значки на доске, бейджи
 *  записи и точки графа баланса берут цвет ОТСЮДА — один источник правды. */
export const Q_COLOR: Record<MoveQuality, string> = {
  brilliant: '#1baca6', great: '#5b8db8', best: '#81b64c', excellent: '#81b64c',
  good: '#95b776', book: '#a88865', forced: '#9aa0a6', inaccuracy: '#f7c631',
  mistake: '#e58f2a', miss: '#ee6b55', blunder: '#fa412d',
};

/** Контекст классификации: оценки, целевая глубина, словарь дебютов. */
export interface ClassifyCtx {
  evals: EvalStore;
  depth: number;
  /** «ECO Название» по FEN или '' (vendor/openings.json). */
  openingName?: (fen: string) => string;
}

function categoryThreshold(key: string, p: number): number {
  const x = Math.max(0, Number(p) || 0);
  if (key === 'best') return Math.max(0, 0.0001 * x * x + 0.0236 * x - 3.7143);
  if (key === 'excellent') return 0.0002 * x * x + 0.1231 * x + 27.5455;
  if (key === 'good') return 0.0002 * x * x + 0.2643 * x + 60.5455;
  if (key === 'inaccuracy') return 0.0002 * x * x + 0.3624 * x + 108.0909;
  if (key === 'mistake') return 0.0003 * x * x + 0.4027 * x + 225.8182;
  return Infinity;
}

function thresholdCategory(prevEval: number, evalLoss: number): MoveQuality {
  for (const key of ['best', 'excellent', 'good', 'inaccuracy', 'mistake', 'blunder'] as const) {
    if (categoryThreshold(key, prevEval) >= evalLoss) return key;
  }
  return 'blunder';
}

function isForcedMove(node: GameNode): boolean {
  const p = node?.parent;
  if (!p?.fen) return false;
  try {
    return new Chess(p.fen).moves().length === 1;
  } catch {
    return false;
  }
}

function isEngineBestMove(node: GameNode, evalLoss: number, prevEval: number, ctx: ClassifyCtx): boolean {
  const p = node?.parent;
  if (!p) return false;
  if (p._bestmove && engineArtifactsValid(p, ctx.depth)) {
    try {
      return uciToSan(nodeFen(p), p._bestmove) === node.san;
    } catch {
      return false;
    }
  }
  return evalLoss <= categoryThreshold('best', prevEval);
}

function mateBoundaryCategory(node: GameNode, before: number, after: number, ctx: ClassifyCtx): MoveQuality | null {
  let beforeMate = moverMate(node, nodeMateWhite(node.parent, ctx.evals, ctx.depth));
  let afterMate = moverMate(node, nodeMateWhite(node, ctx.evals, ctx.depth));
  const beforeCp = moverCp(node, before);
  const afterCp = moverCp(node, after);
  if (beforeMate == null && Math.abs(beforeCp ?? 0) >= MATE_CP) beforeMate = (beforeCp ?? 0) > 0 ? 99 : -99;
  if (afterMate == null && Math.abs(afterCp ?? 0) >= MATE_CP) afterMate = (afterCp ?? 0) > 0 ? 99 : -99;
  const beforeIsMate = beforeMate != null || Math.abs(beforeCp ?? 0) >= MATE_CP;
  const afterIsMate = afterMate != null || Math.abs(afterCp ?? 0) >= MATE_CP;
  if (!beforeIsMate && !afterIsMate) return null;

  if (!beforeIsMate && afterIsMate && afterMate! < 0) {
    const d = Math.abs(afterMate!);
    if ((afterCp ?? 0) >= 0) return 'best';
    if (d <= 2) return 'blunder';
    if (d <= 5) return 'mistake';
    return 'inaccuracy';
  }
  if (beforeIsMate && !afterIsMate && beforeMate! > 0) {
    if ((beforeCp ?? 0) < -600 && (afterCp ?? 0) < -100) return 'best';
    if ((afterCp ?? 0) >= 400) return 'good';
    if ((afterCp ?? 0) >= 150) return 'inaccuracy';
    if ((afterCp ?? 0) >= -100) return 'mistake';
    return 'blunder';
  }
  if (beforeIsMate && afterIsMate) {
    if (beforeMate! > 0 && afterMate! > 0) {
      const delta = Math.abs(afterMate!) - Math.abs(beforeMate!);
      if (delta < 0) return 'best';
      if (delta <= 2) return 'excellent';
      return 'good';
    }
    if (beforeMate! < 0 && afterMate! < 0) {
      return Math.abs(beforeMate!) === Math.abs(afterMate!) ? 'best' : 'good';
    }
    if (beforeMate! > 0 && afterMate! < 0) return Math.abs(afterMate!) >= 4 ? 'mistake' : 'blunder';
    if (beforeMate! < 0 && afterMate! > 0) return 'best';
  }
  return null;
}

function getNodeMove(node: GameNode) {
  const p = node?.parent;
  if (!p || !node.san) return null;
  try {
    const c = new Chess(nodeFen(p));
    return applyLooseMove(c, node.san) || null;
  } catch {
    return null;
  }
}

function destinationHanging(node: GameNode): boolean {
  const move = getNodeMove(node);
  if (!move) return false;
  try {
    const c = new Chess(nodeFen(node));
    const piece = c.get(move.to);
    if (!piece) return false;
    return findHangingPieces(c, piece.color).some((h) => h.square === move.to);
  } catch {
    return false;
  }
}

/** Great («единственный хороший ход») по правилу chess.com: это лучший ход движка,
 *  и любой другой заметно хуже (отрыв топ-линии от 2-й ≥150cp), причём 2-я линия
 *  НЕ оставляет ясного перевеса — то есть именно этот ход держит позицию.
 *  Не зависит от того, зевнул ли соперник (так у chess.com). */
function greatGates(node: GameNode, ctx: ClassifyCtx): boolean {
  const p = node?.parent;
  if (!p || p._secondCp == null) return false;
  if (!engineArtifactsValid(p, ctx.depth)) return false;
  if (nodeMateWhite(p, ctx.evals, ctx.depth) != null || nodeMateWhite(node, ctx.evals, ctx.depth) != null) return false;
  const top = p._bestCp == null ? ctx.evals.get(nodeEvalKey(p)) : p._bestCp;
  if (top == null) return false;
  const topM = p.turn === 'w' ? top : -top;
  const secondM = p.turn === 'w' ? p._secondCp : -p._secondCp;
  if (topM - secondM < 150) return false; // есть явно лучший ход = критичный момент
  if (secondM >= 200) return false; // альтернатива тоже выигрывает → это просто best, не great
  return !destinationHanging(node);
}

/** Brilliant-гейты: лучший ход движка + жертва, которую реально можно взять. */
export function brilliantGates(node: GameNode, ctx: ClassifyCtx): boolean {
  const p = node?.parent;
  if (!p || !node.san || !p._bestmove) return false;
  if (!engineArtifactsValid(p, ctx.depth)) return false;
  const parentFen = nodeFen(p);
  if (uciToSan(parentFen, p._bestmove) !== node.san) return false;

  const after = ctx.evals.get(nodeEvalKey(node));
  if (after == null) return false;
  const mAfter = p.turn === 'w' ? after : -after;
  if (mAfter < 0) return false;
  const second = p._secondCp == null ? null : p.turn === 'w' ? p._secondCp : -p._secondCp;
  if (second != null && second >= 700) return false;
  if (String(node.san).includes('=')) return false;
  try {
    const chess = new Chess(parentFen);
    if (chess.isCheck()) return false;
    const move = applyLooseMove(chess, node.san);
    if (!move) return false;
    const capturedValue = move.captured ? PIECE_VALUES[move.captured] || 0 : 0;
    const hanging = findHangingPieces(chess, p.turn).filter((h) => h.value > capturedValue);
    return hanging.some((h) => hangingCaptureViable(chess.fen(), h, p.turn));
  } catch {
    return false;
  }
}

function isBookNode(node: GameNode, ctx: ClassifyCtx): boolean {
  if (!ctx.openingName) return false;
  // книга = ВСЯ цепочка от корня в базе дебютов
  for (let n: GameNode | null = node; n; n = n.parent) {
    if (n.parent == null) break; // корень не проверяем
    if (!ctx.openingName(nodeFen(n))) return false;
  }
  return !!ctx.openingName(nodeFen(node));
}

function baseLossCategory(node: GameNode, ctx: ClassifyCtx): MoveQuality | null {
  const p = node?.parent;
  if (!p || !node.san) return null;
  const before = ctx.evals.get(nodeEvalKey(p));
  const after = ctx.evals.get(nodeEvalKey(node));
  if (before == null || after == null) return null;
  const mBefore = moverCp(node, before)!;
  const mAfter = moverCp(node, after)!;
  const loss = Math.max(0, mBefore - mAfter);
  const prevEval = Math.abs(before);

  const mateCat = mateBoundaryCategory(node, before, after, ctx);
  let cat: MoveQuality =
    mateCat || (isEngineBestMove(node, loss, prevEval, ctx) ? 'best' : thresholdCategory(prevEval, loss));
  // Miss — community-приближение (chess.com точное правило не публикует).
  if (
    (cat === 'inaccuracy' || cat === 'mistake') &&
    (mBefore >= 200 || (moverMate(node, nodeMateWhite(p, ctx.evals, ctx.depth)) ?? 0) > 0) &&
    mAfter < 100
  ) {
    cat = 'miss';
  }
  if (cat === 'blunder' && (mAfter >= 600 || mBefore <= -600)) cat = 'good';
  return cat;
}

export function classifyMove(node: GameNode | null, ctx: ClassifyCtx): MoveQuality | null {
  if (!node?.parent || !node.san) return null;
  if (isBookNode(node, ctx)) return 'book';
  if (isForcedMove(node)) return 'forced';
  const base = baseLossCategory(node, ctx);
  if (base === 'best') {
    if (node._brilliant) return 'brilliant';
    if (greatGates(node, ctx)) return 'great';
  }
  return base;
}
